import React, { memo, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import getConfig from 'next/config';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import {
    Button,
    Glyphicon,
    StickyContainer,
    StickyElement,
    StickyTrigger,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { ndsLendingOptionsCTAClick } from 'reaxl-analytics-handlers';
import { EmailCTA, OneClickButton } from 'reaxl-email';
import { OwnerPhoneNumber, OwnerTextCTA, OwnerWebsiteCTA } from 'reaxl-molecules';
import { EsntialBuyNowCTA, getEnabledCreditApp, PrequalCTA } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import compareListingsHandler from '@/utilities/compareListingsHandler';
import OwnerWebsiteHandler from '@/utilities/OwnerWebsiteHandler';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import dealsDuck from '@/ducks/dealsDuck.js';
import interestRateDuck from '@/ducks/interestRateDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import userDuck from '@/ducks/userDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';

import useDealerTextMessage from '@/hooks/useDealerTextMessage';

import InventoryPaymentSnapshot from '@/components/vehicledetails/InventoryPaymentSnapshot';

import PsxButton from './psx/PsxButton';

const OwnerContact = memo(({
    analyticsData,
    isCallable,
    disableContactCTAs = false,
    name,
    phone,
    phoneClickHandler,
}) => (
    <div className="align-self-center display-flex flex-column justify-content-center align-items-end text-right text-size-200 text-size-sm-300">
        <Text weight="bold">
            {name}
        </Text>
        {phone && !disableContactCTAs && (
            <OwnerPhoneNumber
                analyticsData={analyticsData}
                phone={phone}
                phoneClickHandler={phoneClickHandler}
                isCallable={isCallable}
            />
        )}
    </div>
));

export function StickyHeaderContainer({
    emailCTAProps,
    existingDeal = {},
    disableContactCTAs = false,
    isSticky = true,
    oneClickCTAProps = {},
    phoneClickHandler,
    prequalCtaProps = {},
}) {

    const { publicRuntimeConfig } = getConfig();
    const {
        compare: [isCompareEnabled],
        est_payments: [enablePriceSnapshotRates, {
            sticky_header_display: stickyHeaderDisplay,
        }],
        ford_estimated_payment: [enableFordEstimatedPayments],

        one_click_lead_vdp: [enableOneClickVDP],
        vdp_sticky_header: [, {
            buttonStyle: stickyBtnStyle,
            style: stickyStyle,
        }],
        wallet_payment_option: [isWalletPaymentOptionEnabled],
    } = useFeatures([
        'compare',
        'est_payments',
        'ford_estimated_payment',
        'one_click_lead_vdp',
        'vdp_sticky_header',
        'wallet_payment_option',
    ]);

    const dispatch = useDispatch();

    const { brands, isBrand } = useBrand();
    const nationalRates = useSelector(interestRateDuck.selectors.getDuckState) || fallbackInterestRates;

    // This should not have been done like this. If it needed to be tested, the brand config could have been turned into
    // an optimizely flag, or, the `ford_estimated_payment` flag could have been set to true for other brands so that the
    // brand config logic took effect.
    const showPaymentsFlag = isBrand(brands.FORD_BRAND) ? enableFordEstimatedPayments && enablePriceSnapshotRates : enablePriceSnapshotRates;

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const listingIdsToCompare = useSelector(compareListingsDuck.selectors.getActiveListingIds);

    const leadProfile = useSelector(userDuck.selectors.getLeadProfile);

    const {
        ecommWebsite = {},
        esntial,
        id: inventoryId,
        marketExtension: { parentOwnerId: parentId } = {},
        owner: {
            dealerPreferredCTA,
            hideEmailForm,
            id: ownerId,
            name,
            phone,
            privateSeller,
            textNumber,
        } = {},
        paymentServices = {},
    } = listing ?? {};

    const {
        oneClickEmailSent,
        handleSendOneClickEmail,
    } = oneClickCTAProps;

    const {
        enableSDPCreditApp,
        enableSACAStickyHeader: enableSACACreditApp,
        ...digitalRetailProps
    } = prequalCtaProps;
    const dealDetails = useSelector((state) => dealsDuck?.selectors?.getTransformedDealDetails(state));
    const ownerCreditApp = useSelector((state) => dealsDuck?.selectors?.getCreditAppDealsByOwnerId?.(state, ownerId));

    const { isSACAEnabled, isSDPEnabled } = getEnabledCreditApp({
        dealDetails,
        ownerCreditApp,
        existingDeal,
        enableSACACreditApp,
        enableSDPCreditApp,
    });

    const isEsntial = !!esntial;
    const ecommLink = Object.keys(ecommWebsite).length > 0;
    const { label } = ecommWebsite;

    const ecommHrefWithWalletData = useSelector(vdpResultsDuck.selectors.getEcommHrefWithWalletData);

    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);

    const handleSACACreditAppClick = useCallback((event) => {
        event.stopPropagation();
        sendClick(ndsLendingOptionsCTAClick, event, { listingId: inventoryId, enableCreditApp: enableSACACreditApp, placement: 'sticky-header' });
        dispatch(vdpActiveInteractionDuck.creators.setKeys({ showFinanceAppModal: true }));
    }, [dispatch, enableSACACreditApp, inventoryId]);

    const ownerTrackingId = parentId ?? ownerId;

    const enableEsntialCTA = isEsntial && !esntial.deemphasized && ecommLink && paymentServices?.digitalRetailingType === 'accelerate';

    const device = useDevice();
    const greaterThanSm = _get(device, 'greaterThan.sm', false);
    const isLessThanMd = _get(device, 'lessThan.md', false);
    const isXs = _get(device, 'is.xs', false);
    const isSm = _get(device, 'is.sm', false);

    const textBody = useDealerTextMessage();

    const analyticsData = {
        inventoryId,
        ownerId,
        placement: 'sticky-header',
    };

    const showCompare = isCompareEnabled && compareToggle;

    // Prequal CTAs should show only on desktop
    const isPrequalEnabled = isSACAEnabled || isSDPEnabled;
    const showPrequalCTA = isPrequalEnabled && !enableEsntialCTA && greaterThanSm;
    const setActiveInteraction = useCallback((key) => {
        dispatch(vdpActiveInteractionDuck.creators.setKeys(key));
    }, [dispatch]);

    const fetchCompareListings = useCallback((listingIds, numRecords, showSpotlight) => {
        dispatch(compareListingsDuck.creators.loadListingsData(listingIds, numRecords, showSpotlight));
    }, [dispatch]);

    // compare currently saved listings and the current vdp listing if it is not already saved
    const handleCompare = useCallback((event) => {
        compareListingsHandler({
            addToActiveResults: false,
            event,
            isAddToCompare: true,
            fetchCompareListings,
            listing,
            listingIds: listingIdsToCompare,
            sendClick,
            setActiveInteraction,
            wasSticky: true,
        });
    }, [fetchCompareListings, listing, listingIdsToCompare, setActiveInteraction]);

    // Hide the sticky header on desktop for non-PSX listings if the dealer doesn't have the email lead form configured.
    if (isSticky && greaterThanSm && hideEmailForm && !privateSeller) {
        return null;
    }

    // Button requirements for SMS test:
    // Esntial: always show website, phone, sms (unless values missing)
    // Ecomm: never show SMS
    // General:
    //      (FF False): show email, phone, sms
    //      (FF True): show only phone, sms if both available, otherwise all available contact methods
    const showTabletContactCtas = stickyHeaderDisplay && isSm;
    const showContactCtas = isXs || showTabletContactCtas;
    const showPhone = showContactCtas && phone?.value && !disableContactCTAs;
    const showSms = showContactCtas && textNumber && (isEsntial || !ecommLink) && !disableContactCTAs;
    // Sticky footer on XS - Sticky header on bigger screen
    const stickyPosition = ((!stickyHeaderDisplay || isXs) && !greaterThanSm) ? 'bottom' : 'top';

    const ctaClassNames = clsx('flex-grow flex-grow-sm-0', {
        'margin-left-auto': phone?.value && textNumber && !showTabletContactCtas,
    });

    // Set of CTAs to show (primary and secondary slot)
    const ctas = [];
    if (privateSeller) {
        ctas.push(
            <div
                key="psxButtons"
                className={clsx(
                    'display-flex justify-content-between gap-2',
                    {
                        'w-100': stickyPosition === 'bottom',
                    }
                )}
            >
                <PsxButton
                    buttonStyle="secondary"
                    uiContext="testDrive"
                    {...(isLessThanMd && { buttonSize: 'small' })}
                    placement={stickyPosition === 'top' ? 'sticky-header' : 'sticky-footer'}
                >
                    Test Drive
                </PsxButton>
                <PsxButton
                    className="flex-grow flex-grow-sm"
                    uiContext="makeOffer"
                    buttonStyle={stickyBtnStyle}
                    style={{ maxWidth: '100%' }} // Override default button max-width applied by sticky header styles
                    {...(isLessThanMd && { buttonSize: 'small' })}
                    placement={stickyPosition === 'top' ? 'sticky-header' : 'sticky-footer'}
                >
                    Make Offer
                </PsxButton>
            </div>
        );
    } else {
        // CTA 1 - Hybrid, Phone, Text, Compare or Prequal CTA
        if (showPrequalCTA) {
            ctas.push(
                <PrequalCTA
                    digitalRetailProps={{
                        ...digitalRetailProps,
                        listing,
                        paymentsDuck,
                    }}
                    enableSDPCreditApp={enableSDPCreditApp}
                    enableSACACreditApp={enableSACACreditApp}
                    existingDeal={existingDeal}
                    dealsDuck={dealsDuck}
                    handleSACACreditAppClick={handleSACACreditAppClick}
                    placement="sticky-header"
                    label="Apply for Financing"
                    key="prequal_cta"
                    {...(isLessThanMd && { buttonSize: 'small' })}
                />
            );
        } else if (showPhone || showSms) {
            if (showPhone) {
                ctas.push(
                    <OwnerPhoneNumber
                        key="phoneCta"
                        analyticsData={analyticsData}
                        className={ctaClassNames}
                        phone={phone}
                        phoneClickHandler={phoneClickHandler}
                        uiContext="button"
                        buttonLabel="Call"
                        {...(isLessThanMd && { bsSize: 'small' })}
                    />
                );
            }

            if (showSms) {
                ctas.push(
                    <OwnerTextCTA
                        analyticsData={analyticsData}
                        className={ctaClassNames}
                        buttonBsStyle="secondary"
                        textClickHandler={phoneClickHandler}
                        key="textCta"
                        label="Text"
                        phoneNumber={textNumber}
                        uiContext="button"
                        textBody={textBody}
                        {...(isLessThanMd && { bsSize: 'small' })}
                    />
                );
            }
        } else if (showCompare) {
            ctas.push(
                <Button
                    key="compareCta"
                    id="btn-comp-sticky"
                    onClick={handleCompare}
                    className="flex-grow flex-grow-sm-0 margin-left-auto margin-left-sm-0"
                    {...(isLessThanMd && { bsSize: 'small' })}
                >
                    <Glyphicon
                        className="margin-right-2"
                        glyph="open"
                        aria-hidden="true"
                    />
                    Compare
                </Button>
            );
        }

        // CTA 2 - Website or Email
        if (dealerPreferredCTA === 'WEBSITE' && isEsntial) {
            ctas.push(
                <EsntialBuyNowCTA
                    dealsDuck={dealsDuck}
                    key="esntialCta"
                    analyticsData={{
                        placement: 'sticky-header',
                        listingId: listing?.id,
                    }}
                    bsStyle="primary"
                    className="flex-grow flex-grow-sm-0"
                    href={ecommWebsite?.href}
                    label={label}
                    listing={listing}
                    nationalRates={nationalRates}
                    paymentsDuck={paymentsDuck}
                    saveDealXgFetcher={saveDealXgFetcher}
                    {...(isLessThanMd && { bsSize: 'small' })}
                    env={publicRuntimeConfig?.environment === 'prod' ? 'prod' : 'staging'}
                    ownerTrackingId={ownerTrackingId}
                />
            );
        } else if (dealerPreferredCTA === 'WEBSITE') {
            ctas.push(
                <OwnerWebsiteCTA
                    key="ownerWebsiteCta"
                    analyticsData={{
                        ...analyticsData,
                        placement: 'sticky-header',
                        ecommLink,
                        label,
                    }}
                    className="flex-grow flex-grow-sm-0"
                    hideIcon={isEsntial}
                    href={ecommHrefWithWalletData}
                    label={label}
                    onClick={OwnerWebsiteHandler({ ecommHref: ecommHrefWithWalletData })}
                    uiContext="button"
                    {...(isLessThanMd && { bsSize: 'small' })}
                />
            );
        } else if (!hideEmailForm) {

            if (enableOneClickVDP && leadProfile?.isOneClickOptedIn) {

                ctas.push(
                    <OneClickButton
                        key="oneClickCta"
                        handleEmailSendClick={handleSendOneClickEmail}
                        hideModifySettings
                        isModifySettingsIcon={false}
                        oneClickEmailSent={oneClickEmailSent}
                    />
                );
            } else {
                ctas.push(
                    <EmailCTA
                        key="emailCta"
                        showIcon={!showContactCtas}
                        type="block"
                        context="stickyHeader"
                        labelKey="checkAvailability"
                        {...emailCTAProps}
                        {...(isLessThanMd && { buttonSize: 'small' })}
                        buttonStyle={stickyBtnStyle}
                        className="flex-grow flex-grow-sm-0"
                    />
                );
            }
        }
    }

    // Sticky header should not show when no CTAs are enabled
    if (isSticky && !ctas.length) { return null; }

    const stickyHeaderElmt = (
        <div className={clsx(stickyStyle, 'padding-vertical-3')}>
            <div className="container">
                <div className="display-flex align-items-center justify-content-between">
                    <div>
                        {!isXs && (stickyHeaderDisplay ? (
                            <InventoryPaymentSnapshot
                                existingDeal={existingDeal}
                                isWalletPaymentOptionEnabled={isWalletPaymentOptionEnabled}
                                listing={listing}
                                showPayment={showPaymentsFlag}
                                showPaymentCTA={enablePriceSnapshotRates}
                            />
                        ) : (
                            <OwnerContact
                                analyticsData={analyticsData}
                                isCallable={isSm}
                                disableContactCTAs={disableContactCTAs}
                                name={name}
                                phoneClickHandler={phoneClickHandler}
                                phone={phone}
                            />
                        ))}
                    </div>
                    <div className="display-flex align-items-center justify-content-end gap-3 flex-grow">
                        {ctas}
                    </div>
                </div>
            </div>
        </div>
    );

    return isSticky ? (
        <StickyContainer>
            <StickyTrigger
                showOnLeave="top"
                hideOnEnter="top"
            />
            <StickyElement
                id="sticky-header-vdp"
                position={stickyPosition}
            >
                {stickyHeaderElmt}
            </StickyElement>

        </StickyContainer>
    ) : (
        <StickyElement
            id="sticky-header-vdp"
            position={stickyPosition}
        >
            {stickyHeaderElmt}
        </StickyElement>
    );
}

export default memo(StickyHeaderContainer);
